import { useEffect, useMemo, useRef, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Button, DatePicker, Flex, Form, Input, Select, Space, Typography } from 'antd';
import { isEmpty, omitBy } from 'lodash';
import { useRequest, useSetState } from 'ahooks';
import Permission, { useAccess } from '@/components/permission';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from '@/components/ui/pagination';

import TrustEditModal, { type TrustEditFormValues } from '@/components/trust-edit-modal';
import TrustCell from '@/components/trust-cell';

import { updateUserTrust, userExport, userListApi } from '@/services/user';
import { formatDramaTimestamp } from '@/pages/drama-review/utils';
import {
    filterTrustNotDefault,
    parseTrustValue,
    type TrustFilterMode,
} from '@/utils/trust';
import { resolveApiResult } from '@/utils/apiResponse';
import { handleExportResponse } from '@/utils/export-response';

/**
 * Types
 */
import type { FormProps } from 'antd';
import type { Dayjs } from 'dayjs';
import type { User, UserListParams } from '@/services/user';
import type { ProTableProps } from '@ant-design/pro-components';
import type { PaginationProps } from '@/components/ui/pagination';

const USER_TRUST_PERMISSION = 'Users_Users_Trust';
const USER_EXPORT_PERMISSION = 'Users_Users_Export';

type State = {
    dataSource: User[];
    selectedRowKeys: React.Key[];
    searchAfter: string | undefined;
    hasMore: boolean;
};

type SearchFormValues = {
    nickname?: string;
    id?: string;
    created_at_range?: [Dayjs, Dayjs];
};

/**
 * Constants
 */
const defaultPayload: UserListParams = {
    nickname: undefined,
    id: undefined,
};

const buildUserListParams = (values: SearchFormValues): UserListParams => {
    const { created_at_range, ...rest } = values;
    const next: UserListParams = {
        ...defaultPayload,
        ...omitBy(rest, isEmpty),
    };

    if (created_at_range?.length === 2) {
        next.created_at = [
            created_at_range[0].startOf('d').toISOString(),
            created_at_range[1].endOf('d').toISOString(),
        ];
    }

    return next;
};

const formatAddressMiddle = (address: string) => {
    if (address.length <= 8) return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

const UserInfoCell: React.FC<{ record: User }> = ({ record }) => {
    const address = record.wallet_address?.trim();

    return (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <div>
                <FormattedMessage id="p.uu.columns.uid" />：
                {record.id ? (
                    <Typography.Text copyable={{ text: record.id }}>{record.id}</Typography.Text>
                ) : (
                    '-'
                )}
            </div>
            <div>
                <FormattedMessage id="p.uu.columns.nickname" />：{record.nickname || '-'}
            </div>
            <div>
                <FormattedMessage id="p.uu.columns.address" />：
                {address ? (
                    <Typography.Text copyable={{ text: address }}>{formatAddressMiddle(address)}</Typography.Text>
                ) : (
                    '-'
                )}
            </div>
        </Space>
    );
};

const PageAccountManagement: React.FC = () => {
    /**
     * Hooks
     */
    const { message } = App.useApp();

    const { formatMessage } = useIntl();
    const access = useAccess();

    const canManageTrust = access(USER_TRUST_PERMISSION);

    const [formSearch] = Form.useForm<SearchFormValues>();
    const [searchParams] = useSearchParams();
    const deeplinkAppliedRef = useRef(false);
    const searchRef = useRef<HTMLDivElement>(null);

    const [trustFilter, setTrustFilter] = useState<TrustFilterMode>('all');
    const [trustModalOpen, setTrustModalOpen] = useState(false);
    const [trustEditingRecord, setTrustEditingRecord] = useState<User | null>(null);
    const [updatingTrustId, setUpdatingTrustId] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    const [state, setState] = useSetState<State>({
        dataSource: [],
        selectedRowKeys: [],
        hasMore: false,
        searchAfter: undefined,
    });

    /**
     * Paginations
     */
    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        hasMore: false,
        searchAfter: '',
        size: 20,
        total: 0,
        onChange: ({ page, searchAfter, size }) => {
            setPagination({
                current: page,
                size: size,
                searchAfter,
            });
        },
    });

    const [payload, setPayload] = useSetState<UserListParams>({});

    /**
     * Requests
     */
    const {
        loading,
        refresh: refreshUserList,
        run: runUserList,
    } = useRequest(userListApi, {
        manual: true,
        onSuccess: ({ data: { code, data, msg, meta } }) => {
            if (code !== 200) {
                return message.error(msg);
            }

            setPagination({
                size: meta.size,
                total: meta.total,
                hasMore: meta.more,
                searchAfter: meta.cursor || '',
            });

            setState({
                dataSource: data,
            });
        },
    });

    const displayedDataSource = useMemo(
        () => (trustFilter === 'notDefault' ? filterTrustNotDefault(state.dataSource) : state.dataSource),
        [state.dataSource, trustFilter],
    );

    const trustNotDefaultCount = useMemo(
        () => filterTrustNotDefault(state.dataSource).length,
        [state.dataSource],
    );

    const handleTrustEdit = (record: User) => {
        if (!canManageTrust) return;
        setTrustEditingRecord(record);
        setTrustModalOpen(true);
    };

    const handleTrustSubmit = async (values: TrustEditFormValues) => {
        const userId = trustEditingRecord?.id;
        if (!userId) return;

        setUpdatingTrustId(userId);
        try {
            const { data } = await updateUserTrust(userId, values);
            const result = resolveApiResult(data);
            if (!result.success) {
                message.error(result.msg || formatMessage({ id: 'trust.updateFailed' }));
                return;
            }

            message.success(formatMessage({ id: 'trust.updateSuccess' }));
            setTrustModalOpen(false);
            setTrustEditingRecord(null);
            refreshUserList();
        } catch (error: any) {
            message.error(
                error?.response?.data?.msg || error?.message || formatMessage({ id: 'trust.updateFailed' }),
            );
        } finally {
            setUpdatingTrustId(null);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const { data } = await userExport(payload);
            handleExportResponse(data, formatMessage({ id: 'p.uu.msg.exportTips' }));
        } catch (error: any) {
            message.error(error?.response?.data?.msg || error?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const formSearchProps: FormProps<SearchFormValues> = {
        form: formSearch,
        layout: 'inline',
        onFinish: (values) => {
            setPagination({
                current: 1,
                size: pagination.size,
                searchAfter: '',
            });

            setPayload(buildUserListParams(values));
        },
    };

    const proTableProps: ProTableProps<User, any> = {
        bordered: true,
        columns: [
            {
                dataIndex: 'index',
                valueType: 'index',
                title: 'No.',
                width: 50,
                fixed: 'left',
            },
            {
                title: <FormattedMessage id="p.uu.columns.userInfo" />,
                width: 320,
                render: (_, record) => <UserInfoCell record={record} />,
            },
            {
                title: <FormattedMessage id="trust.columnTitle" />,
                dataIndex: 'trust',
                width: 140,
                sorter: (a, b) => parseTrustValue(a.trust) - parseTrustValue(b.trust),
                render: (_, record) => (
                    <TrustCell
                        trust={record.trust}
                        editable={Boolean(canManageTrust)}
                        loading={updatingTrustId === record.id}
                        onEdit={() => handleTrustEdit(record)}
                    />
                ),
            },
            {
                dataIndex: 'created_at',
                width: 200,
                title: <FormattedMessage id="p.uu.columns.registrationTime" />,
                render: (_, record) => formatDramaTimestamp(record.created_at),
            },
        ],
        dataSource: displayedDataSource,
        loading,
        pagination: false,
        options: {
            reload: refreshUserList,
        },
        toolBarRender: () => [
            trustNotDefaultCount > 0 ? (
                <Typography.Text key="trust-summary" type="secondary">
                    {formatMessage({ id: 'trust.summary' }, { count: trustNotDefaultCount })}
                </Typography.Text>
            ) : null,
            <Select<TrustFilterMode>
                key="trust-filter"
                value={trustFilter}
                style={{ width: 160 }}
                options={[
                    {
                        label: formatMessage({ id: 'trust.filter.all' }),
                        value: 'all',
                    },
                    {
                        label: formatMessage({ id: 'trust.filter.notDefault' }),
                        value: 'notDefault',
                    },
                ]}
                onChange={setTrustFilter}
            />,
            <Permission permission={USER_EXPORT_PERMISSION} key="export">
                <Button loading={exporting} onClick={handleExport}>
                    <FormattedMessage id="p.uu.btn.export" />
                </Button>
            </Permission>,
        ],
        rowKey: 'id',
        scroll: {
            x: 780,
            y: `calc(100vh - 224px - ${searchRef.current?.clientHeight || 64}px - 16px - 48px - 16px)`,
        },
        search: false,
        tableAlertRender: false,
    };

    /**
     * Effects
     */
    useEffect(() => {
        if (deeplinkAppliedRef.current) return;
        const id = searchParams.get('id')?.trim();
        if (!id) return;
        deeplinkAppliedRef.current = true;
        formSearch.setFieldsValue({ id });
        setPagination({ current: 1, searchAfter: undefined });
        setPayload({ id });
    }, [formSearch, searchParams, setPagination, setPayload]);

    useEffect(() => {
        runUserList({
            size: pagination.size,
            search_after: pagination.searchAfter,
            ...payload,
        });
    }, [pagination.current, pagination.size, pagination.searchAfter, payload]);

    return (
        <Permission permission="Users_Users">
            <>
                <ProCard className="mb-16" ref={searchRef}>
                    <Form {...formSearchProps}>
                        <Flex gap={16} wrap>
                            <Form.Item name="nickname" style={{ marginBottom: 0 }}>
                                <Input
                                    allowClear
                                    placeholder={formatMessage({ id: 'p.uu.columns.nickname' })}
                                    style={{ width: 200 }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="id"
                                style={{ marginBottom: 0 }}
                                getValueFromEvent={(e) => String(e?.target?.value ?? '').replace(/\D/g, '')}
                            >
                                <Input
                                    allowClear
                                    inputMode="numeric"
                                    placeholder={formatMessage({ id: 'p.uu.columns.uid' })}
                                    style={{ width: 200 }}
                                />
                            </Form.Item>
                            <Form.Item name="created_at_range" style={{ marginBottom: 0 }}>
                                <DatePicker.RangePicker
                                    allowClear
                                    placeholder={[
                                        formatMessage({ id: 'p.uu.search.createdAtStart' }),
                                        formatMessage({ id: 'p.uu.search.createdAtEnd' }),
                                    ]}
                                />
                            </Form.Item>
                            <Button loading={loading} type="primary" onClick={() => formSearch.submit()}>
                                <FormattedMessage id="b.search" />
                            </Button>
                            <Button
                                onClick={() => {
                                    formSearch.resetFields();
                                    setPagination({
                                        current: 1,
                                        size: pagination.size,
                                        searchAfter: '',
                                    });
                                    setPayload({});
                                }}
                            >
                                <FormattedMessage id="b.reset" />
                            </Button>
                        </Flex>
                    </Form>
                </ProCard>

                <AdminProTable {...proTableProps} />

                <Pagination {...pagination} isLoading={loading} />

                <TrustEditModal
                    open={trustModalOpen}
                    title={formatMessage({ id: 'trust.editUserTitle' })}
                    initialTrust={trustEditingRecord?.trust}
                    loading={Boolean(trustEditingRecord && updatingTrustId === trustEditingRecord.id)}
                    onCancel={() => {
                        setTrustModalOpen(false);
                        setTrustEditingRecord(null);
                    }}
                    onSubmit={handleTrustSubmit}
                />
            </>
        </Permission>
    );
};

export default PageAccountManagement;
